import { ChartCanvas, Chart } from "@react-financial-charts/core";
import { XAxis, YAxis } from "@react-financial-charts/axes";
import { LineSeries } from "@react-financial-charts/series";
import { CrossHairCursor, MouseCoordinateX, MouseCoordinateY } from "@react-financial-charts/coordinates";
import { format } from "d3-format";
import type { FlowsPointDto } from "../api";
import { CHART_HEIGHT, FII_COLOR, DII_COLOR, useContainerWidth, buildScale, NotEnoughPoints } from "./flowsChartShared";

/** TAB 3 "Rolling correlation" -- the most analytically valuable lens for
 * "does VIX drive FII/DII over time" (as opposed to Tab 1's single number
 * for the whole window): a 20-TRADING-DAY trailing Pearson correlation of
 * VIX day-change vs FII net, and vs DII net, each rendered as a line so a
 * viewer can see WHEN the fear->flows relationship holds, breaks, or flips
 * sign -- not just what it averages to.
 *
 * Computed server-side (app/api/routes/market.py's _flows_series) over a
 * TRAILING TRADING-DAY window measured in trading_calendar POSITIONS, never
 * "the last 20 array rows" -- a window that happens to straddle one of the
 * 29 Arihant gap days is honestly under-populated (fewer real pairs
 * contribute), never silently treated as 20 real consecutive days. Below
 * ROLLING_CORR_MIN_N (10) real pairs in that trailing window, the backend
 * emits `null` rather than a number computed on too little to mean
 * anything -- rendered here as a genuine BREAK in the line (the yAccessor
 * returns `undefined`), not a flat/interpolated segment, so a thin
 * stretch of data reads as missing, not as "correlation near zero."
 */

interface ChartDatum {
  date: Date;
  rolling_corr_fii: number | null;
  rolling_corr_dii: number | null;
  rolling_corr_n: number | null;
}

const ROLLING_WINDOW_LABEL = "20-trading-day trailing";

export function FlowsRollingCorrChart({ points }: { points: FlowsPointDto[] }) {
  const { ref, width } = useContainerWidth<HTMLDivElement>();

  const data: ChartDatum[] = points.map((p) => ({
    date: new Date(p.date), rolling_corr_fii: p.rolling_corr_fii, rolling_corr_dii: p.rolling_corr_dii,
    rolling_corr_n: p.rolling_corr_n,
  }));

  if (data.length < 2 || width === 0) {
    return data.length < 2 ? <NotEnoughPoints containerRef={ref} /> : <div ref={ref} style={{ minHeight: 40 }} />;
  }

  const { plotData, xScale, xAccessor, displayXAccessor, xExtents, formatTickDate } = buildScale(data);
  const anyRolling = data.some((d) => d.rolling_corr_fii != null);
  const nValues = data.map((d) => d.rolling_corr_n).filter((n): n is number => n != null);
  const minN = nValues.length ? Math.min(...nValues) : null;
  const maxN = nValues.length ? Math.max(...nValues) : null;

  return (
    <div ref={ref}>
      <div className="flows-legend">
        <span><i style={{ background: FII_COLOR }} /> corr(VIX-change, FII net)</span>
        <span><i style={{ background: DII_COLOR }} /> corr(VIX-change, DII net)</span>
      </div>
      {!anyRolling ? (
        <div className="empty-state" style={{ padding: 8 }}>
          No stretch of this window has enough consecutive trading days with both a real VIX change and a flow
          reading (need at least 10 in a {ROLLING_WINDOW_LABEL} window) to compute a rolling correlation.
        </div>
      ) : (
        <>
          <ChartCanvas
            height={CHART_HEIGHT}
            width={width}
            ratio={window.devicePixelRatio || 1}
            margin={{ left: 40, right: 8, top: 8, bottom: 24 }}
            data={plotData}
            seriesName="flows-rolling-corr"
            xScale={xScale}
            xAccessor={xAccessor}
            displayXAccessor={displayXAccessor}
            xExtents={xExtents}
          >
            <Chart id={0} height={CHART_HEIGHT} yExtents={[-1, 1]} padding={{ top: 10, bottom: 10 }}>
              <XAxis showGridLines tickFormat={formatTickDate("%d %b")} />
              <YAxis showGridLines tickFormat={format("+.1f")} />
              <LineSeries yAccessor={(d: ChartDatum) => d.rolling_corr_fii ?? undefined} strokeStyle={FII_COLOR} strokeWidth={1.6} />
              <LineSeries yAccessor={(d: ChartDatum) => d.rolling_corr_dii ?? undefined} strokeStyle={DII_COLOR} strokeWidth={1.6} />
              <MouseCoordinateY displayFormat={format("+.2f")} />
              <MouseCoordinateX displayFormat={formatTickDate("%d %b %Y")} />
            </Chart>
            <CrossHairCursor />
          </ChartCanvas>
          <div className="flows-series-note">
            {ROLLING_WINDOW_LABEL} correlation, gap-aware (a window straddling one of the source's known gap days
            uses only the real pairs it actually has, never a padded 20) -- {minN != null && maxN != null
              ? `pair count per point in this window ranges ${minN}-${maxN}`
              : ""}. A break in a line means that window did not have enough real pairs (minimum 10) to report a
            value.
          </div>
        </>
      )}
    </div>
  );
}
